import React from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';

import { parseShortcodeArray } from '../../../helpers/shortcode';
import { renderPossiblyOptimizedImage } from '../../../helpers/images';

import { media, colors } from '../../../styles/constants';

import PhonesImageContainer from './PhonesImageContainer';
import TabletImageContainer from './TabletImageContainer';
import CombinedImageContainer from './CombinedImageContainer';

const imageContainerTypes = ['tablet', 'phones', 'combined'];

const getImageContainerByType = type => {
  switch (type) {
    case 'phones':
      return PhonesImageContainer;
    case 'tablet':
      return TabletImageContainer;
    case 'combined':
      return CombinedImageContainer;
    default:
      throw new Error(
        `Unable to find suitable image container for type: ${type}. Types are: ${imageContainerTypes.join(
          ', '
        )}`
      );
  }
};

const CircleSvg = styled.svg`
  position: absolute;
  z-index: -1;
  width: 37rem;
  height: 37rem;
  top: -10rem;

  ${props =>
    props.top
      ? css`
          /* This is not pretty but it does the job */
          @media (min-width: ${media.workdetail2ndBreakpoint}) and (max-width: ${media.tabletPortrait}) {
            top: ${props.top};
          }
        `
      : css`
          top: 50%;
          transform: translate(0, -50%);
        `}



  @media (min-width: ${media.tabletPortrait}) {
    top: 50%;
    transform: translate(0, -50%);
    width: 45rem;
    height: 45rem;
    left: -6rem;
    margin-top: -0.5rem;
  }
`;

const Circle = ({ color, ...props }) => {
  return (
    <CircleSvg {...props} viewBox="0 0 460 460">
      <circle
        fill={color}
        opacity="0.9"
        cx="230.16623"
        cy="230.295697"
        r="229.534939"
      />
    </CircleSvg>
  );
};

Circle.propTypes = {
  color: PropTypes.string
};

const CaptionContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  color: ${colors.white};
  min-height: 15rem;
  max-width: 37rem;

  && p {
    margin-bottom: 0;
    padding-top: 2rem;
    font-size: 2.2rem;
    line-height: 3rem;
    text-align: center;
    margin-right: 2rem;
    margin-left: 2rem;
  }

  @media (min-width: ${media.workdetail2ndBreakpoint}) {
    ${props =>
      !props.verticalCenter &&
      css`
        height: 33rem;
      `}
  }

  @media (min-width: ${media.tabletPortrait}) {
    justify-content: initial;

    ${props =>
      !props.verticalCenter &&
      css`
        height: 30rem;
      `}

    && p {
      padding: 0 3rem 0 2rem;
      text-align: initial;
      margin-left: 0rem;
      margin-right: 1rem;
      font-size: 3rem;
      line-height: 3.9rem;
    }
  }

  @media (min-width: ${media.tabletLandscape}) {
    ${props =>
      !props.verticalCenter &&
      css`
        height: 48rem;
      `}

    && p {
      margin-left: 0.5rem;
      font-size: 3.4rem;
      line-height: 4.5rem;
      padding: 0 2rem 0 2.5rem;
      max-width: 46rem;
    }
  }

  @media (min-width: ${media.desktop}) {
    padding-right: 5rem;
  }
`;

const ImagesWithCircleContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 5rem;
  margin-bottom: 12rem;
  min-height: 30rem;

  && .gatsby-image-wrapper {
    padding: 0;
    margin: 0;
  }

  ${props =>
    props.verticalCenter &&
    css`
      align-items: center;
    `}

    @media (min-width: ${media.phone}) {
      margin-bottom: 14rem;
    }

  @media (min-width: ${media.workdetail2ndBreakpoint}) {
    flex-direction: row;
    min-height: 36rem;
    margin-bottom: 6rem;
    margin-top: 10rem;

    > div:first-child {
      flex-grow: 1;
      flex-shrink: 0;
      flex-basis: 60%;
    }

    > div:last-child {
      flex-grow: 0;
      flex-shrink: 1;
      flex-basis: 40%;
    }
  }


  @media (min-width: ${media.tabletLandscape}) {
    min-height: 46rem;
  }
`;

const ImagesWithCircle = ({
  type,
  images,
  caption,
  color,
  optimizedImages
}) => {
  const imagesArray = parseShortcodeArray(images);
  const circleColor = colors[color] ? colors[color] : color;
  const ImagesContainer = getImageContainerByType(type);

  return (
    <ImagesWithCircleContainer verticalCenter={type !== 'combined'}>
      <ImagesContainer>
        {imagesArray.map((image, i) => (
          <div key={`${image.alt}-${i}`}>
            {renderPossiblyOptimizedImage(image, optimizedImages)}
          </div>
        ))}
      </ImagesContainer>
      <CaptionContainer verticalCenter={type !== 'combined'}>
        <Circle top={type === 'combined' && '-2rem'} color={circleColor} />
        <p>{caption}</p>
      </CaptionContainer>
    </ImagesWithCircleContainer>
  );
};

ImagesWithCircle.propTypes = {
  type: PropTypes.oneOf(imageContainerTypes),
  images: PropTypes.string,
  caption: PropTypes.string,
  color: PropTypes.string,
  optimizedImages: PropTypes.array
};

export default ImagesWithCircle;
